// DTOs del rol ADMIN TENANT (A1-A5). Configuración estable tenant-wide.
// A6 (auditoría) reusa auditoriaQuerySchema + coordinadorAuditoriaSchema.
//
// Convención: desactivar/soft-delete exige `razon` (≥20 chars) + se audita.
import { z } from 'zod';
import { formatoLoteAyudaSchema, formatoLoteEjemploSchema, formatoLoteRegexSchema, } from './formato_lote.js';
// categoriaParoEnumLegacy ya no se importa — admin usa categoria_id (FK uuid) ahora.
export const adminRazonSchema = z
    .string()
    .trim()
    .min(20, 'La razón debe tener al menos 20 caracteres')
    .max(500);
// =============================================================================
// A1 — Tenant
// =============================================================================
export const adminTenantSchema = z.object({
    id: z.string().uuid(),
    nombre: z.string(),
    nit: z.string(),
    logo_url: z.string().nullable(),
    contacto_email: z.string(),
    plan: z.string(),
    activo: z.boolean(),
    timezone: z.string(),
    atribucion_turno_cruce: z.enum(['dia_inicio', 'dia_fin']),
    dia_inicio_semana: z.number().int().min(1).max(7),
    /**
     * Meta de facturación mensual en MILLONES de COP (Informe Gerencial,
     * Robotpack 2026-09). Config estable → la edita el Admin Tenant. Null =
     * sin meta configurada (el informe omite la línea de meta).
     */
    meta_facturacion_mensual_millones: z.number().positive().nullable(),
    /** Formato del lote de captura (B7, migración 097). */
    formato_lote_regex: z.string(),
    formato_lote_ejemplo: z.string(),
    formato_lote_ayuda: z.string(),
});
/**
 * Forma de un nombre IANA (`Region/Ciudad`, `Etc/GMT+5`, `UTC`). Descarta
 * offsets sueltos tipo `+05:00`, que `Intl` acepta pero Postgres interpreta
 * distinto. La EXISTENCIA real de la zona la revalida el API con `Intl`.
 */
const IANA_TZ_FORMA = /^[A-Za-z][A-Za-z0-9_+-]*(?:\/[A-Za-z0-9_+-]+)*$/;
export const adminTenantUpdateSchema = z
    .object({
    nombre: z.string().trim().min(1).max(120),
    nit: z.string().trim().min(5).max(30),
    logo_url: z.string().url().nullable(),
    contacto_email: z.string().email().toLowerCase().trim(),
    /** Millones de COP. Null = quitar la meta. */
    meta_facturacion_mensual_millones: z.number().positive().max(99_999_999.99).nullable(),
    // ---- Configuración operativa (afecta cómo se AGRUPAN los períodos) ----
    // Cambiar estos tres campos NO reescribe ningún registro histórico: solo
    // cambia cómo las consultas derivan el día y la semana operativa, así que
    // períodos ya cerrados pueden re-agruparse distinto. El aviso al usuario
    // vive en A1ConfigTenantPage.
    /** Zona horaria IANA del tenant, ej. 'America/Bogota'. */
    timezone: z.string().trim().min(1).max(64).regex(IANA_TZ_FORMA, 'timezone IANA inválida'),
    /** Turno que cruza medianoche: se atribuye al día en que inicia o al que termina. */
    atribucion_turno_cruce: z.enum(['dia_inicio', 'dia_fin']),
    /** Día ISO en que arranca la semana operativa: 1 = lunes … 7 = domingo. */
    dia_inicio_semana: z.number().int().min(1).max(7),
    // ---- Formato del lote de captura (B7) ----
    // Solo afecta capturas NUEVAS o ediciones: los lotes ya guardados no se
    // revalidan. El API combina con los valores guardados y exige que la
    // expresión compile y que el ejemplo la cumpla (`validarFormatoLoteConfig`).
    formato_lote_regex: formatoLoteRegexSchema,
    formato_lote_ejemplo: formatoLoteEjemploSchema,
    formato_lote_ayuda: formatoLoteAyudaSchema,
})
    .partial();
// =============================================================================
// A4 — Clientes
// =============================================================================
export const adminClienteSchema = z.object({
    id: z.string().uuid(),
    nombre: z.string(),
    nit: z.string().nullable(),
    codigo_externo_sap: z.string().nullable(),
    es_principal: z.boolean(),
    activo: z.boolean(),
});
export const adminClientesListSchema = z.object({
    tenant: z.object({ id: z.string().uuid(), nombre: z.string() }),
    clientes: z.array(adminClienteSchema),
});
export const adminClienteCreateSchema = z.object({
    nombre: z.string().trim().min(1).max(120),
    nit: z.string().trim().max(30).nullable().optional(),
    codigo_externo_sap: z.string().trim().max(40).nullable().optional(),
    es_principal: z.boolean().optional(),
});
export const adminClienteUpdateSchema = z
    .object({
    nombre: z.string().trim().min(1).max(120),
    nit: z.string().trim().max(30).nullable(),
    codigo_externo_sap: z.string().trim().max(40).nullable(),
    es_principal: z.boolean(),
    activo: z.boolean(),
    razon: adminRazonSchema,
})
    .partial()
    .refine((d) => d.activo !== false || (d.razon && d.razon.length >= 20), {
    message: 'Desactivar requiere razón (≥20 chars)',
    path: ['razon'],
});
// =============================================================================
// A3 — Causales de paro (jerarquía 2 niveles)
// =============================================================================
export const adminCausalSchema = z.object({
    id: z.string().uuid(),
    parent_id: z.string().uuid().nullable(),
    /** FK a `categorias_paro.id` — reemplaza el enum `categoria_madre`. */
    categoria_id: z.string().uuid(),
    codigo: z.string(),
    nombre: z.string(),
    requiere_observacion: z.boolean(),
    activa: z.boolean(),
});
export const adminCausalNodeSchema = adminCausalSchema.extend({
    hijas: z.array(adminCausalSchema),
});
export const adminCausalesListSchema = z.object({
    tenant: z.object({ id: z.string().uuid(), nombre: z.string() }),
    causales: z.array(adminCausalNodeSchema),
});
export const adminCausalCreateSchema = z.object({
    parent_id: z.string().uuid().nullable(),
    /** FK a `categorias_paro.id`. La pertenencia al tenant se valida en la API via RLS. */
    categoria_id: z.string().uuid(),
    codigo: z.string().trim().min(1).max(40),
    nombre: z.string().trim().min(1).max(120),
    requiere_observacion: z.boolean().optional(),
});
export const adminCausalUpdateSchema = z
    .object({
    nombre: z.string().trim().min(1).max(120),
    requiere_observacion: z.boolean(),
    activa: z.boolean(),
    razon: adminRazonSchema,
})
    .partial();
// =============================================================================
// A2 — Estructura (plantas → áreas → líneas)
// =============================================================================
export const adminLineaSchema = z.object({
    id: z.string().uuid(),
    area_id: z.string().uuid(),
    planta_id: z.string().uuid(),
    nombre: z.string(),
    codigo: z.string(),
    activa: z.boolean(),
});
export const adminAreaSchema = z.object({
    id: z.string().uuid(),
    planta_id: z.string().uuid(),
    nombre: z.string(),
    codigo: z.string(),
    activa: z.boolean(),
    lineas: z.array(adminLineaSchema),
});
export const adminPlantaSchema = z.object({
    id: z.string().uuid(),
    nombre: z.string(),
    codigo: z.string(),
    direccion: z.string().nullable(),
    latitud: z.number().nullable(),
    longitud: z.number().nullable(),
    activa: z.boolean(),
    areas: z.array(adminAreaSchema),
});
export const adminEstructuraSchema = z.object({
    tenant: z.object({ id: z.string().uuid(), nombre: z.string() }),
    plantas: z.array(adminPlantaSchema),
});
export const adminPlantaCreateSchema = z.object({
    nombre: z.string().trim().min(1).max(120),
    codigo: z.string().trim().min(1).max(40),
    direccion: z.string().trim().max(200).nullable().optional(),
    latitud: z.number().min(-90).max(90).nullable().optional(),
    longitud: z.number().min(-180).max(180).nullable().optional(),
});
export const adminAreaCreateSchema = z.object({
    nombre: z.string().trim().min(1).max(120),
    codigo: z.string().trim().min(1).max(40),
});
export const adminLineaCreateSchema = z.object({
    area_id: z.string().uuid(),
    nombre: z.string().trim().min(1).max(120),
    codigo: z.string().trim().min(1).max(40),
});
// Update común para planta/área/línea: campos editables + activa+razon
export const adminEstructuraUpdateSchema = z
    .object({
    nombre: z.string().trim().min(1).max(120),
    codigo: z.string().trim().min(1).max(40),
    direccion: z.string().trim().max(200).nullable(),
    latitud: z.number().min(-90).max(90).nullable(),
    longitud: z.number().min(-180).max(180).nullable(),
    activa: z.boolean(),
    razon: adminRazonSchema,
})
    .partial();
// =============================================================================
// A5 — Usuarios + asignación de plantas/líneas
// =============================================================================
/** Roles que el Admin Tenant puede gestionar (NO directivo/admin/super). */
export const adminRolGestionableSchema = z.enum(['coordinador', 'supervisor', 'lider']);
export const adminUsuarioSchema = z.object({
    user_id: z.string().uuid(),
    email: z.string().email(),
    nombre: z.string(),
    apellido: z.string(),
    rol: z.string(),
    activo: z.boolean(),
    plantas: z.array(z.object({ id: z.string().uuid(), nombre: z.string() })),
    lineas: z.array(z.object({ id: z.string().uuid(), codigo: z.string() })),
});
export const adminUsuariosListSchema = z.object({
    tenant: z.object({ id: z.string().uuid(), nombre: z.string() }),
    usuarios: z.array(adminUsuarioSchema),
    recursos: z.object({
        plantas: z.array(z.object({ id: z.string().uuid(), nombre: z.string(), codigo: z.string() })),
        lineas: z.array(z.object({ id: z.string().uuid(), codigo: z.string(), planta_id: z.string().uuid() })),
    }),
});
export const adminUsuarioCreateSchema = z
    .object({
    email: z.string().email().toLowerCase().trim(),
    nombre: z.string().trim().min(1).max(80),
    apellido: z.string().trim().min(1).max(80),
    rol: adminRolGestionableSchema,
    planta_ids: z.array(z.string().uuid()).optional().default([]),
    linea_ids: z.array(z.string().uuid()).optional().default([]),
})
    .refine((d) => (d.rol === 'lider' ? d.linea_ids.length > 0 : d.planta_ids.length > 0), {
    message: 'coordinador/supervisor requieren ≥1 planta; líder requiere ≥1 línea',
    path: ['planta_ids'],
});
export const adminUsuarioUpdateSchema = z
    .object({
    nombre: z.string().trim().min(1).max(80),
    apellido: z.string().trim().min(1).max(80),
    activo: z.boolean(),
    razon: adminRazonSchema,
})
    .partial();
export const adminReasignarPlantasSchema = z.object({
    planta_ids: z.array(z.string().uuid()).min(1),
    razon: adminRazonSchema,
});
export const adminReasignarLineasSchema = z.object({
    linea_ids: z.array(z.string().uuid()).min(1),
    razon: adminRazonSchema,
});
//# sourceMappingURL=admin.js.map